import { useCallback, useEffect, useRef, useState } from 'react'
import type mapboxgl from 'mapbox-gl'
import { APPROX_AREA_RADIUS_KM } from '@fellowship/shared'
import { MAPBOX_TOKEN } from '../lib/geocode'
import { reportMapWebglFallback } from '../lib/mapWebgl'

interface EventLocationMapProps {
  lat: number
  lng: number
  label?: string
  height?: number
  // When true, the exact location is hidden: render a soft area circle instead of a
  // pin, zoom out, and drop the "open in Google Maps" link (it would imply precision).
  approximate?: boolean
  radiusKm?: number
}

// 64-sided polygon approximating a circle of radiusKm around (lat,lng). Mirrors the
// helper used by the admin coverage map.
function makeCircleGeoJSON(lat: number, lng: number, radiusKm: number) {
  const n = 64
  const coords: [number, number][] = []
  for (let i = 0; i <= n; i++) {
    const angle = (2 * Math.PI * i) / n
    const dlat = (radiusKm / 111.32) * Math.sin(angle)
    const dlng = (radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180))) * Math.cos(angle)
    coords.push([lng + dlng, lat + dlat])
  }
  return {
    type: 'FeatureCollection' as const,
    features: [
      { type: 'Feature' as const, geometry: { type: 'Polygon' as const, coordinates: [coords] }, properties: {} },
    ],
  }
}

function useMapInstance(
  containerRef: React.RefObject<HTMLDivElement | null>,
  lat: number,
  lng: number,
  interactive: boolean,
  enabled: boolean,
  approximate: boolean,
  radiusKm: number,
  onWebglError: () => void,
) {
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markerRef = useRef<mapboxgl.Marker | null>(null)
  const useMapbox = Boolean(MAPBOX_TOKEN) && import.meta.env.MODE !== 'test'

  useEffect(() => {
    if (!enabled || !containerRef.current || !useMapbox) return
    let cancelled = false

    // Any throw here (most commonly `Failed to initialize WebGL` from `new mb.Map`)
    // must be caught, not left as an unhandled rejection — flip to the OSM fallback.
    const handleInitError = (err: unknown) => {
      if (cancelled) return
      reportMapWebglFallback(err)
      onWebglError()
    }

    void import('mapbox-gl').then((mod) => {
      if (cancelled || !containerRef.current) return
      try {
        const mb = mod.default
        mb.accessToken = MAPBOX_TOKEN as string

        const map = new mb.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [lng, lat],
          // Zoom out for an approximate area so the circle reads as "somewhere around
          // here", not a precise point.
          zoom: approximate ? 12 : 14,
          interactive,
          attributionControl: false,
        })

        mapRef.current = map

        map.on('load', () => {
          if (cancelled) return
          map.resize()
          if (approximate) {
            map.addSource('event-area', { type: 'geojson', data: makeCircleGeoJSON(lat, lng, radiusKm) })
            map.addLayer({
              id: 'event-area-fill',
              type: 'fill',
              source: 'event-area',
              paint: { 'fill-color': '#3B4CCA', 'fill-opacity': 0.12 },
            })
            map.addLayer({
              id: 'event-area-stroke',
              type: 'line',
              source: 'event-area',
              paint: { 'line-color': '#3B4CCA', 'line-width': 2, 'line-opacity': 0.6 },
            })
          } else {
            markerRef.current = new mb.Marker({ color: '#3B4CCA' }).setLngLat([lng, lat]).addTo(map)
          }
        })
      } catch (err) {
        handleInitError(err)
      }
    }).catch(handleInitError)

    return () => {
      cancelled = true
      markerRef.current?.remove()
      markerRef.current = null
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [enabled, useMapbox, lat, lng, interactive, containerRef, approximate, radiusKm, onWebglError])

  return useMapbox
}

const BTN =
  'w-8 h-8 rounded-lg bg-white/90 backdrop-blur border border-black/10 flex items-center justify-center cursor-pointer text-ink hover:bg-white transition-colors shadow-sm'

export function EventLocationMap({
  lat,
  lng,
  label,
  height = 280,
  approximate = false,
  radiusKm = APPROX_AREA_RADIUS_KM,
}: EventLocationMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const fsContainerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Runtime WebGL init failure → fall back to the OSM iframe below.
  const [webglFailed, setWebglFailed] = useState(false)
  const handleWebglError = useCallback(() => setWebglFailed(true), [])
  const useMapbox = Boolean(MAPBOX_TOKEN) && import.meta.env.MODE !== 'test' && !webglFailed

  useMapInstance(containerRef, lat, lng, false, !webglFailed, approximate, radiusKm, handleWebglError)
  useMapInstance(fsContainerRef, lat, lng, true, isFullscreen && !webglFailed, approximate, radiusKm, handleWebglError)

  const gmapsUrl = `https://www.google.com/maps?q=${lat},${lng}`
  // Widen the fallback bbox for an approximate area so the iframe doesn't read as a
  // precise point, and omit the exact marker.
  const osmPad = approximate ? 0.03 : 0.012
  const osmBbox = `${lng - osmPad},${lat - osmPad},${lng + osmPad},${lat + osmPad}`
  const osmMarker = approximate ? '' : `&marker=${lat}%2C${lng}`
  const osmSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(osmBbox)}&layer=mapnik${osmMarker}`

  return (
    <>
      <div style={{ position: 'relative', height, borderRadius: 14, overflow: 'hidden' }}>
        {useMapbox ? (
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        ) : (
          <iframe
            title={label ? `Map showing ${label}` : 'Event location map'}
            src={osmSrc}
            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        )}

        {/* Overlay buttons — top-right */}
        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            type="button"
            onClick={() => setIsFullscreen(true)}
            className={BTN}
            aria-label="Expand map"
            title="Expand map"
          >
            <ExpandIcon />
          </button>
          {/* No precise link for an approximate area. */}
          {!approximate && (
            <a
              href={gmapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={BTN}
              aria-label="Open in Google Maps"
              title="Open in Google Maps"
            >
              <GoogleMapsIcon />
            </a>
          )}
        </div>

        {/* Approximate-area caption */}
        {approximate && (
          <div
            style={{ position: 'absolute', left: 10, bottom: 10 }}
            className="px-2.5 py-1.5 rounded-lg bg-white/90 backdrop-blur border border-black/10 text-[11.5px] font-medium text-ink shadow-sm"
          >
            Approximate area · exact location shared after approval
          </div>
        )}
      </div>

      {/* Full-screen modal */}
      {isFullscreen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.82)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setIsFullscreen(false) }}
        >
          <div style={{ position: 'relative', width: '90vw', height: '80vh', borderRadius: 16, overflow: 'hidden' }}>
            {useMapbox ? (
              <div ref={fsContainerRef} style={{ width: '100%', height: '100%' }} />
            ) : (
              <iframe
                title={label ? `Map showing ${label}` : 'Event location map'}
                src={osmSrc}
                style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            )}

            {/* Fullscreen overlay buttons */}
            <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className={BTN}
                aria-label="Close map"
                title="Close map"
              >
                <CloseIcon />
              </button>
              {!approximate && (
                <a
                  href={gmapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={BTN}
                  aria-label="Open in Google Maps"
                  title="Open in Google Maps"
                >
                  <GoogleMapsIcon />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

function ExpandIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function GoogleMapsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#3B4CCA" stroke="none" />
      <circle cx="12" cy="9" r="2.5" fill="white" stroke="none" />
    </svg>
  )
}
